import "./DealStructure.css";

import { Dispatch, SetStateAction, useContext, useEffect, useMemo, useState } from "react";
import { v4 as uuidv4 } from "uuid";

import { DealSpecificProperty, DealSpecificSchema, InvestmentDetailsInterface, SelectData } from "../../../../models";
import { searchDealSpecificSchemas, SearchDealSpecificSchemasResponse } from "../../../../service/query";
import { ServiceError, Status } from "../../../../service/Shared";
import {
  camelCaseToSentenceCase,
  getDealStructureValidationErrorFieldKeys,
  hasDealStructureValidationError,
  sentenceOrPascalToCamel,
} from "../../../../utils";
import { NewDealJourneyContext } from "../NewDealJourneyContext";

interface UseDealStructureReturnData {
  errors: ServiceError[] | undefined;
  investments: InvestmentDetailsInterface[];
  selectedInvestmentKey: string | undefined;
  selectedInvestment: InvestmentDetailsInterface | undefined;
  dealSpecificSchemaProperties: [string, DealSpecificProperty][] | undefined;
  instrumentSelectData: SelectData;
  associationSelectData: SelectData;
  hasValidationError: (investmentKey: string | undefined, fieldKey: string) => boolean;
  handleAddInvestment: () => void;
  handleUpdateInvestment: <K extends keyof InvestmentDetailsInterface>(
    key: string,
    field: K,
    value:
      | InvestmentDetailsInterface[K]
      | ((currentValue: InvestmentDetailsInterface[K]) => InvestmentDetailsInterface[K])
  ) => void;
  handleRemoveInvestment: (key: string) => void;
  setSelectedInvestmentKey: Dispatch<SetStateAction<string | undefined>>;
}

export const useDealStructure = (): UseDealStructureReturnData => {
  const { formValues, errors, setFormValues } = useContext(NewDealJourneyContext);
  const investments = formValues.structureInvestments ?? [];
  const [selectedInvestmentKey, setSelectedInvestmentKey] = useState<string | undefined>(
    formValues.structureInvestments?.[0]?.key
  );

  const [searchDealSpecificSchemasData, setSearchDealSpecificSchemasData] =
    useState<SearchDealSpecificSchemasResponse["results"]>();
  const [instrumentSelectData, setInstrumentSelectData] = useState<SelectData>([]);

  const selectedInvestment = useMemo(
    () => investments.find((investment) => investment.key === selectedInvestmentKey) ?? undefined,
    [investments, selectedInvestmentKey]
  );

  const handleAddInvestment = (): void => {
    const newInvestment = {
      key: uuidv4(),
      instrument: undefined,
      associations: [],
      description: undefined,
      fields: undefined,
    } as InvestmentDetailsInterface;

    setSelectedInvestmentKey(newInvestment.key);

    setFormValues((prev) => ({
      ...prev,
      structureInvestments: [...(prev.structureInvestments ?? []), newInvestment],
    }));
  };

  const handleUpdateInvestment = <K extends keyof InvestmentDetailsInterface>(
    key: string,
    field: K,
    value:
      | InvestmentDetailsInterface[K]
      | ((currentValue: InvestmentDetailsInterface[K]) => InvestmentDetailsInterface[K])
  ): void => {
    setFormValues((prev) => ({
      ...prev,
      structureInvestments: (prev.structureInvestments ?? []).map((investment) => {
        if (investment.key !== key) {
          return investment;
        }

        const currentValue = investment[field];
        const nextValue = typeof value === "function" ? value(currentValue) : value;

        return {
          ...investment,
          [field]: nextValue,
        };
      }),
    }));
  };

  const handleRemoveInvestment = (key: string): void => {
    const filteredInvestments = investments.filter((investment) => investment.key !== key);
    setSelectedInvestmentKey(filteredInvestments[0]?.key);
    setFormValues((prev) => ({
      ...prev,
      structureInvestments: filteredInvestments,
    }));
  };

  const associationSelectData = useMemo<SelectData>(() => {
    const { associations, projectDrafts, landholdingDrafts, companyDraft } = formValues;
    const options = new Map<string, string>();
    const landholdingProjectKeys = new Set(landholdingDrafts.flatMap((draft) => draft.projectKeys));

    const addOption = (key: string | undefined, value: string | undefined): void => {
      if (!key || !value) return;
      options.set(key, value);
    };

    associations.forEach((association) => {
      addOption(association.objectUuid, association.name);
    });

    if (companyDraft?.name) {
      addOption(companyDraft.key, companyDraft.name);
    }

    projectDrafts.forEach((draft) => {
      if (landholdingProjectKeys.has(draft.key)) return;
      addOption(draft.projectUuid ?? draft.key, draft.name);
    });

    landholdingDrafts.forEach((draft) => {
      addOption(draft.key, draft.name);
    });

    return Array.from(options, ([key, value]) => ({ key, value }));
  }, [formValues]);

  const errorFieldKeys = useMemo(
    () => getDealStructureValidationErrorFieldKeys(errors, investments),
    [errors, investments]
  );

  const dealSpecificSchemaProperties = useMemo(() => {
    if (!searchDealSpecificSchemasData) return undefined;

    const schema = searchDealSpecificSchemasData.find(
      (x) => sentenceOrPascalToCamel(x.dealType) === selectedInvestment?.instrument
    );

    if (!schema?.definition) return undefined;

    const parsed = JSON.parse(schema.definition) as DealSpecificSchema;
    return Object.entries(parsed?.properties ?? {});
  }, [selectedInvestment?.instrument, searchDealSpecificSchemasData]);

  useEffect(() => {
    (async () => {
      const response = await searchDealSpecificSchemas({
        paging: {
          afterCursor: null,
          beforeCursor: null,
          limit: 25,
        },
        sort: [{ key: "results.dealType", direction: "asc" }],
      });

      if (response.status === Status.Success && response.data) {
        setSearchDealSpecificSchemasData(response.data.results);
        setInstrumentSelectData(
          response.data.results.map((x) => ({
            key: sentenceOrPascalToCamel(x.dealType),
            value: camelCaseToSentenceCase(x.dealType),
          })) as SelectData
        );
      }
    })();
  }, []);

  useEffect(() => {
    if (!selectedInvestmentKey && investments.length > 0) {
      setSelectedInvestmentKey(investments[0].key);
    }
  }, [investments, selectedInvestmentKey]);

  return {
    errors,
    investments,
    selectedInvestment,
    selectedInvestmentKey,
    associationSelectData,
    instrumentSelectData,
    dealSpecificSchemaProperties,
    hasValidationError: (investmentKey: string | undefined, fieldKey: string) =>
      hasDealStructureValidationError(errorFieldKeys, investmentKey, fieldKey),
    setSelectedInvestmentKey,
    handleAddInvestment,
    handleUpdateInvestment,
    handleRemoveInvestment,
  };
};
